import styles from '../index.module.css'

import { FaRegSadTear } from '@react-icons/all-files/fa/FaRegSadTear'
import React from 'react'

import { Button, Flex, Table, Text } from 'components/ds'
import Skeleton from 'components/ui/skeleton'

import { TableRow } from './table-row'

export const TableBody: React.FC<{
  data: Record<string, any>[]
  loading?: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  hasNextPageError?: boolean
  onRetryNextPage?: () => void
  table
  rowActionsOpen
  isPartOfMultipleSelection
  handleTrackClick
  selectedRowItems
  columns
  sentinelRef
  skeletonRef
  getPinningStyles
  location
  searchType
  searchResultsSource
  searchId
  selectableIndexByRowId?: Map<string, number>
}> = ({
  data,
  loading,
  isFetchingNextPage,
  hasNextPage,
  hasNextPageError = false,
  onRetryNextPage,
  table,
  rowActionsOpen,
  isPartOfMultipleSelection,
  handleTrackClick,
  selectedRowItems,
  columns,
  sentinelRef,
  skeletonRef,
  getPinningStyles,
  location,
  searchType,
  searchResultsSource,
  searchId,
  selectableIndexByRowId,
}) => {
  return (
    <Table.Body>
      {/* Show skeleton rows when loading new search results */}
      {loading &&
        !isFetchingNextPage &&
        // Generate 12 skeleton rows for initial loading
        Array(12)
          .fill(0)
          .map((_, index) => (
            <Table.Row
              key={`skeleton-${index}`}
              ref={index === 0 ? skeletonRef : undefined}
              className={styles.SkeletonRow}
              style={{
                animationDelay: `${index * 0.02}s`,
              }}
            >
              {table.getVisibleLeafColumns().map((column, colIndex) => (
                <Table.Cell
                  key={colIndex}
                  className={styles.TableCell}
                  style={{
                    minWidth: column.getSize(),
                    height: '56px', // Fixed height for skeleton rows
                    padding: '16px 8px', // Consistent padding
                  }}
                >
                  <Skeleton type="block" lines={1} />
                </Table.Cell>
              ))}
            </Table.Row>
          ))}
      {!data?.length && !loading && !isFetchingNextPage ? (
        <Table.Row>
          <Table.Cell colSpan={columns.length} align="center">
            <Flex p="9" gap="1" align="center" justify="center">
              <FaRegSadTear />
              <Text weight="bold" color="gray">
                No results found.
              </Text>
            </Flex>
          </Table.Cell>
        </Table.Row>
      ) : null}
      {/* Render data rows only when not loading new search results */}
      {data?.length > 0 &&
        !loading &&
        (() => {
          const rows = table.getRowModel().rows
          const leafColumnCount = table.getVisibleLeafColumns().length
          const spacerRow = (
            <Table.Row className={styles.NestedBlockSpacer} aria-hidden="true">
              <Table.Cell colSpan={leafColumnCount} />
            </Table.Row>
          )
          return (
            <>
              {rows.map((row, i) => {
                const flatIndex = selectableIndexByRowId?.get(row.id) ?? i
                const multipleSelected = isPartOfMultipleSelection(flatIndex)
                const isNestedBlockStart =
                  row.depth > 0 && (i === 0 || rows[i - 1].depth === 0)
                const isNestedBlockEnd =
                  row.depth > 0 &&
                  (i === rows.length - 1 || rows[i + 1].depth === 0)
                return (
                  <React.Fragment key={row.id}>
                    {isNestedBlockStart && spacerRow}
                    <TableRow
                      row={row}
                      flatIndex={flatIndex}
                      rowActionsOpen={rowActionsOpen}
                      rowIsSelected={row.getIsSelected()}
                      visibleColumns={table.getVisibleLeafColumns()}
                      isPartOfMultipleSelection={multipleSelected}
                      handleTrackClick={handleTrackClick}
                      selectedRowItems={selectedRowItems}
                      getPinningStyles={getPinningStyles}
                      location={location}
                      searchType={searchType}
                      searchResultsSource={searchResultsSource}
                      searchId={searchId}
                      // Also getCanExpand: a row can be toggled before its
                      // children load, and tinting it then reads as expanded
                      // when nothing has opened.
                      isExpanded={row.getIsExpanded() && row.getCanExpand()}
                    />
                    {isNestedBlockEnd && spacerRow}
                  </React.Fragment>
                )
              })}
              {/* A failed next page swaps the sentinel skeletons for a retry row, so
              the observer has nothing to re-observe until the user asks again */}
              {hasNextPage && hasNextPageError ? (
                <Table.Row>
                  <Table.Cell colSpan={columns.length} align="center">
                    <Flex p="5" gap="3" align="center" justify="center">
                      <Text size="2" color="gray">
                        Something went wrong loading more results.
                      </Text>
                      {onRetryNextPage ? (
                        <Button
                          size="1"
                          variant="soft"
                          onClick={onRetryNextPage}
                        >
                          Retry
                        </Button>
                      ) : null}
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ) : null}
              {/* Skeleton rows for infinite scroll - first row is sentinel */}
              {hasNextPage &&
                !hasNextPageError &&
                Array(5)
                  .fill(0)
                  .map((_, index) => (
                    <Table.Row
                      key={`infinite-skeleton-${index}`}
                      ref={index === 0 ? sentinelRef : undefined}
                      className={styles.SkeletonRow}
                      style={{
                        animationDelay: `${index * 0.02}s`,
                      }}
                    >
                      {table.getVisibleLeafColumns().map((column, colIndex) => (
                        <Table.Cell
                          key={colIndex}
                          className={styles.TableCell}
                          style={{
                            minWidth: column.getSize(),
                            height: '56px',
                            padding: '16px 8px',
                          }}
                        >
                          <Skeleton type="block" lines={1} />
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  ))}
            </>
          )
        })()}
    </Table.Body>
  )
}
